/**
 * ComputationResult component displays computation results in a data grid format.
 * This component handles loading computations, their results, and displaying them in an
 * interactive data grid with grouping, filtering, and export capabilities.
 */

import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "hooks/useAuth";
import { horusApi } from "services/api/horus";
import ResultGrid from "components/common/pyramid/ResultGrid";
import { useGroupedItems } from "hooks/useGroupedItems";
import { useFormatConverter } from "hooks/useFormatConverter";

/**
 * ComputationResult component for displaying and interacting with computation results.
 * Uses the common ResultGrid component to display data with advanced filtering,
 * grouping, and export capabilities.
 */
const ComputationResult = () => {
  /**
   * State management for the component
   */
  const [computations, setComputations] = useState([]);
  const [selectedComputation, setSelectedComputation] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial data fetch
  const [isLoadingData, setIsLoadingData] = useState(false); // Loading state for grid data
  const [gridData, setGridData] = useState([]);
  const [headers, setHeaders] = useState({}); // Headers configuration for the grid
  const [expandedHeaders, setExpandedHeaders] = useState({}); // Tracks which header groups are expanded
  const [flatHeaders, setFlatHeaders] = useState([]); // Flat list of headers for format conversion
  const [toDate, setToDate] = useState([]); // Data for the excel export
  const [description, setDescription] = useState([]); // Data for the excel export
  const { accessToken, userList, companyList } = useAuth();
  const api = horusApi();

  // Grouping computations by user
  const groupedComputations = useGroupedItems(
    computations,
    userList,
    companyList
  );

  // Convert headers formats using the hook
  const convertedColumns = useFormatConverter(flatHeaders);

  // Create a map of fieldName -> converted format for easy lookup
  const formatMap = useMemo(() => {
    const map = {};
    convertedColumns.forEach((col) => {
      map[col.dataField] = {
        format: col.format,
        dataType: col.dataType,
      };
    });
    return map;
  }, [convertedColumns]);

  /**
   * Effect to load header configurations from the API
   * This determines the structure of columns in the data grid
   */
  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        if (!accessToken) return;

        const data = await api.getResultHeaderComputation();

        if (data) {
          const headers = Array.isArray(data) ? data : data.result || [];

          // Store flat headers for format conversion
          setFlatHeaders(headers);

          // Group headers by parent caption
          const groupedHeaders = headers.reduce((acc, header) => {
            const parentCaption = header.parentHeader.caption;
            if (!acc[parentCaption]) {
              acc[parentCaption] = {
                headers: [],
                canBeExpanded: header.parentHeader.canBeExpanded,
                rank: header.parentHeader.rank,
              };
            }
            acc[parentCaption].headers.push(header);
            return acc;
          }, {});

          // Ordering columns within each group by rank
          Object.keys(groupedHeaders).forEach((parentCaption) => {
            groupedHeaders[parentCaption].headers.sort((a, b) => {
              const rankA = a.rank || 0;
              const rankB = b.rank || 0;
              return rankA - rankB;
            });
          });

          setHeaders(groupedHeaders);

          // Initialize expanded state for header groups
          const initialExpandedState = Object.keys(groupedHeaders).reduce(
            (acc, key) => {
              acc[key] = false;
              return acc;
            },
            {}
          );
          setExpandedHeaders(initialExpandedState);
        }
      } catch (error) {
        console.error("Error during header loading:", error);
      }
    };

    fetchHeaders();
  }, [accessToken]);

  /**
   * Effect to load available computations from the API
   */
  useEffect(() => {
    const fetchComputations = async () => {
      try {
        if (!accessToken) return;

        const data = await api.getComputations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const computations = Array.isArray(data) ? data : data.result || [];

          const sortedComputations = [...computations].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setComputations(sortedComputations);
        }
      } catch (error) {
        console.error("Error during computation loading:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComputations();
  }, [accessToken]);

  /**
   * Effect to load results for the selected computation
   * Triggered when a new computation is selected
   */
  useEffect(() => {
    const fetchResults = async () => {
      if (!selectedComputation || !accessToken) return;
      setIsLoadingData(true);
      try {
        const rawData = await api.getComputationResult(selectedComputation);

        const computation = await api.getComputation({
          id: selectedComputation,
          includeUserTransitiveGroups: true,
        });

        try {
          const parsedComputation = JSON.parse(computation);

          const description =
            parsedComputation?.result?.description ||
            parsedComputation?.description ||
            "Description not available";

          setDescription(description);
        } catch (computationParseError) {
          console.error(
            "Error during computation parsing:",
            computationParseError
          );
          setDescription("Description not available");
        }

        try {
          const parsedData = JSON.parse(rawData);
          const dataArray = Array.isArray(parsedData)
            ? parsedData
            : parsedData.result || [];
          setGridData(dataArray);

          if (dataArray.length > 0 && dataArray[0].ToDateComputation) {
            setToDate("_" + dataArray[0].ToDateComputation);
          } else {
            setToDate("");
          }
        } catch (parseError) {
          console.error("Error during data processing:", parseError);
          setGridData([]);
          setToDate("");
        }
      } catch (error) {
        console.error("Error during results retrieval:", error);
        setGridData([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchResults();
  }, [selectedComputation, accessToken]);

  /**
   * Toggles the expansion state of a header group
   * @param {string} parentCaption - The caption of the parent header
   */
  const toggleHeaderExpansion = (parentCaption) => {
    setExpandedHeaders((prev) => ({
      ...prev,
      [parentCaption]: !prev[parentCaption],
    }));
  };

  return (
    <ResultGrid
      items={computations}
      groupedItems={groupedComputations}
      selectedItemId={selectedComputation}
      onItemChange={setSelectedComputation}
      isLoading={isLoading}
      isLoadingData={isLoadingData}
      gridData={gridData}
      headers={headers}
      expandedHeaders={expandedHeaders}
      toggleHeaderExpansion={toggleHeaderExpansion}
      setExpandedHeaders={setExpandedHeaders}
      formatMap={formatMap}
      keyExprField="FifoKey"
      itemSelectLabel="Choose your computation"
      resultType="Computation"
      toDate={toDate}
      description={description}
      grouped={true}
      moduleName="HORUS"
    />
  );
};

export default ComputationResult;
